import type { BranchKey, WorkspaceKey } from '@festina-life/api'
import type { SetRequiredAndNonNullable } from '@festina-life/core'
import { IconButton } from '@festina-life/ui/actions'
import { toast } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { useState } from 'react'

import type { ConciseChangesetWithBranchKey } from '../../../../uiModels/changesets/types'
import { useWorkspaceManagerContext } from '../../../../workspace/workspaceManagerContext'
import { useOpenActiveElement } from '../../useOpenActiveElement'
import type { BranchDetailsNavigationState } from '../branchDetailsNavigationState'
import { PullChangesetToWorkspaceModal } from './PullChangesetToWorkspaceModal'

type Props = {
  branchKey: BranchKey
  changeset: SetRequiredAndNonNullable<
    ConciseChangesetWithBranchKey,
    'changesetKey'
  >
}

export const PullChangesetButton = ({ branchKey, changeset }: Props) => {
  const translate = useTranslate()
  const { onSwitch } = useWorkspaceManagerContext()
  const openActiveElement = useOpenActiveElement()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handlePulled = async (workspaceKey: WorkspaceKey) => {
    setIsModalOpen(false)
    toast({
      title: translate('changesetList.pullToWorkspace.success', {
        workspaceName: workspaceKey.name,
      }),
      variant: 'success',
    })
    // Switch to the new workspace first so it becomes the current workspace,
    // carrying navigation state so the unified branch/workspace panel lands on
    // the "Changes" tab on arrival...
    await onSwitch(workspaceKey, {
      state: {
        initialTab: 'operations',
      } satisfies BranchDetailsNavigationState,
    })
    // ...then select it in the branches tree (opening the branches panel). When
    // the pull creates a second workspace on the branch the unified
    // branch/workspace panel is no longer used, so this ensures the new
    // workspace's details panel is in scope.
    openActiveElement(workspaceKey)
  }

  return (
    <>
      <IconButton
        iconName="ArrowUturnLeft"
        tip={translate('changesetList.pullToWorkspace.tooltip')}
        aria-label={translate('changesetList.pullToWorkspace.tooltip')}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          setIsModalOpen(true)
        }}
      />
      <PullChangesetToWorkspaceModal
        isOpen={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onPulled={handlePulled}
        branchKey={branchKey}
        changeset={changeset}
      />
    </>
  )
}
